import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { Component } from '@theme/component';

class ProductInventory extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updateInventory);
    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);
    // Initialize stock display by fetching live inventory
    this.fetchAndDisplayInventory();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updateInventory);
    document.removeEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);
  }

  /**
   * Updates the inventory when the variant changes.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateInventory = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newInventory = event.detail.data.html.querySelector('product-inventory');

    if (!newInventory) return;

    // Morph the children
    morph(this, newInventory, { childrenOnly: true });

    // Update dataset attributes from the morphed server html
    this.dataset.inventoryQuantity = newInventory.dataset.inventoryQuantity || '0';
    this.dataset.inventoryManaged = newInventory.dataset.inventoryManaged || 'false';

    // Re-fetch live inventory for the new variant
    this.fetchAndDisplayInventory();
  };

  /**
   * Handles the cart update event.
   */
  handleCartUpdate = (event) => {
    const cart = event.detail?.resource;
    this.updateStockDisplay(null, cart);
  };

  /**
   * Gets the current variant ID from the page.
   */
  getVariantId() {
    const input = document.querySelector('product-form-component input[name="id"], input[name="id"]');
    return input ? input.value : null;
  }

  /**
   * Gets the product handle from the page URL.
   */
  getProductHandle() {
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  enforceQuantityMax(maxQty) {
    if (maxQty === undefined || maxQty === null || maxQty < 0) return;
    const input = document.querySelector('quantity-selector-component input[ref="quantityInput"], quantity-selector-component input');
    const plusBtn = document.querySelector('quantity-selector-component button[name="plus"]');
    if (input) {
      input.max = maxQty;
      // If current value exceeds max, clamp it
      if (parseInt(input.value) > maxQty) {
        input.value = maxQty;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    if (plusBtn && input) {
      const currentVal = parseInt(input.value) || 0;
      plusBtn.disabled = currentVal >= maxQty;
    }
  }

  /**
   * Fetches the product's live inventory from the Shopify storefront API,
   * then updates both the display text and enforces the quantity max.
   */
  fetchAndDisplayInventory() {
    const isTracked = this.dataset.inventoryManaged === 'true';
    const stockQty = parseInt(this.dataset.inventoryQuantity);

    // If inventory is not tracked by Shopify, show "In stock" and don't cap
    if (!isTracked) {
      const textEl = this.querySelector('.product-inventory__text');
      const iconWrapper = this.querySelector('.product-inventory__icon');
      if (textEl) textEl.textContent = 'In stock';
      if (iconWrapper) {
        iconWrapper.className = 'svg-wrapper product-inventory__icon product-inventory__icon-in_stock';
      }
      return;
    }

    // Enforce quantity selector max
    if (stockQty !== null && !isNaN(stockQty) && stockQty > 0) {
      this.enforceQuantityMax(stockQty);
    }

    // Update display with current cart state
    this.updateStockDisplay(stockQty, null);
  }

  /**
   * Updates the remaining stock display text.
   * @param {number|null} stockQty - The total stock (from API). If null, reads from dataset.
   * @param {object|null} cart - Cart data (optional, fetched if null).
   */
  updateStockDisplay = (stockQty, cart) => {
    const variantId = this.getVariantId();
    if (!variantId) return;

    const isTracked = this.dataset.inventoryManaged === 'true';
    const dbInventory = stockQty !== null ? stockQty : parseInt(this.dataset.inventoryQuantity);

    const updateWithCart = (cartData) => {
      const cartItem = cartData?.items?.find(item => item.variant_id.toString() === variantId.toString());
      const cartQty = cartItem ? cartItem.quantity : 0;

      const textEl = this.querySelector('.product-inventory__text');
      const iconWrapper = this.querySelector('.product-inventory__icon');

      if (textEl) {
        if (!isTracked || isNaN(dbInventory)) {
          // Untracked inventory or invalid quantity — show "In stock"
          textEl.textContent = 'In stock';
          if (iconWrapper) {
            iconWrapper.className = 'svg-wrapper product-inventory__icon product-inventory__icon-in_stock';
          }
        } else {
          const remaining = Math.max(0, dbInventory - cartQty);
          if (dbInventory <= 0 || remaining <= 0) {
            textEl.textContent = 'Out of stock';
            if (iconWrapper) {
              iconWrapper.className = 'svg-wrapper product-inventory__icon product-inventory__icon-out_of_stock';
            }
            this.enforceQuantityMax(0);
          } else {
            textEl.textContent = `${remaining} Product${remaining === 1 ? '' : 's'} Left`;
            if (iconWrapper) {
              iconWrapper.className = `svg-wrapper product-inventory__icon product-inventory__icon-${remaining <= 5 ? 'low' : 'in_stock'}`;
            }
            this.enforceQuantityMax(dbInventory);
          }
        }
      }
    };

    if (cart) {
      updateWithCart(cart);
    } else {
      fetch('/cart.js')
        .then(res => res.json())
        .then(updateWithCart)
        .catch(err => console.error('Failed to fetch cart for stock display:', err));
    }
  };
}

if (!customElements.get('product-inventory')) {
  customElements.define('product-inventory', ProductInventory);
}
